import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { User } from '@core/models';
import { Store } from '@ngxs/store';
import { SharedModule } from '@shared/shared.module';
import { TaskActions } from '@tasks/state/actions';
import { Task } from '@tasks/tasks.types';
import { TsButtonModule } from 'tsui';

@Component({
  selector: 'app-task-details-followers',
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyButtonModule,
    MatIconModule,
    MatLegacyCheckboxModule,
    OverlayModule,
    SharedModule,
    MatDividerModule,
    TsButtonModule,
  ],
  template: `
    <div>
      <p class="text-base font-medium mt-2">Followers</p>
      <mat-divider role="separator" class="mat-divider mt-1 mat-divider-horizontal" aria-orientation="horizontal">
      </mat-divider>
      <div style="margin-left: -9px">
        <div
          class="bg-black-50 hover:bg-black-100 text-cool-gray-500 mx-2 px-2 py-1 rounded-sm inline-block my-2"
          *ngFor="let user of task?.followings"
        >
          <span class="tag-title">{{ user.name }}</span>
        </div>
      </div>
      <div>
        <!-- [tsOverlayTriggerFor]="overlayBox" -->
        <div #followersPanelOrigin class="my-2">
          <ts-button
            type="flat"
            color="basic"
            [disabled]="disabled"
            [icon]="task?.followings?.length > 0 ? 'edit' : 'add'"
            (click)="openFollowersPanel()"
          >
            {{ task?.followings?.length > 0 ? 'Edit' : 'Add' }}
          </ts-button>
        </div>

        <!-- Followers Panel -->
        <ng-template #followersPanel>
          <div
            class="tasks-details-tags-panel p-2 rounded shadow-lg bg-white tasks-details-tags-panel ng-star-inserted"
          >
            <div class="tags-panel-header border-b pb-3 mb-3">
              <div class="new-tag flex items-center">
                <mat-icon [svgIcon]="'search'"></mat-icon>
                <input
                  type="text"
                  placeholder="Enter user name"
                  (input)="filtersUsers($event)"
                  [maxLength]="50"
                  #newTagInput
                />
              </div>
            </div>

            <div class="available-tags">
              <!-- Followers -->
              <ng-container>
                <mat-checkbox
                  *ngFor="let user of filteredUsers; trackBy: trackByFn"
                  [color]="'primary'"
                  [checked]="task?.following_user_ids?.includes(user.id)"
                  (change)="toggleTaskFollower(user, $event)"
                  [disabled]="!user?.active"
                >
                  {{ user.name }}
                </mat-checkbox>
              </ng-container>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  styleUrl: './task-details-followers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsFollowersComponent {
  @Input() users: User[];
  @Input() task: Task;
  @Input() disabled: boolean;

  store = inject(Store);

  private followersPanelOverlayRef: OverlayRef;
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  protected filteredUsers;
  private destroyRef = inject(DestroyRef);

  @ViewChild('followersPanelOrigin')
  private _followersPanelOrigin;

  @ViewChild('followersPanel')
  private _followersPanel: TemplateRef<any>;

  openFollowersPanel(): void {
    // Create the overlay
    this.followersPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this._followersPanelOrigin?.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });
    // Subscribe to the attachments observable
    this.followersPanelOverlayRef
      .attachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Focus to the search input once the overlay has been attached
        this.followersPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    // Create a portal from the template
    const templatePortal = new TemplatePortal(this._followersPanel, this.viewContainerRef);

    // Attach the portal to the overlay
    this.followersPanelOverlayRef.attach(templatePortal);

    // Subscribe to the backdrop click
    this.followersPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // If overlay exists and attached...
        if (this.followersPanelOverlayRef && this.followersPanelOverlayRef.hasAttached()) {
          // Detach it
          this.followersPanelOverlayRef.detach();
          // Reset the users filter
          this.filteredUsers = this.users;
        }

        // If template portal exists and attached...
        if (templatePortal && templatePortal.isAttached) {
          // Detach it
          templatePortal.detach();
        }
      });
  }

  filtersUsers(event): void {
    // Get the value
    const value = event.target.value.toLowerCase();

    // Filter the usersw
    this.filteredUsers =
      value.trim() !== '' ? this.users.filter((user) => user.name.toLowerCase().includes(value)) : this.users;
  }

  toggleTaskFollower(user: User, change: MatCheckboxChange): void {
    if (change.checked) {
      this.store.dispatch(new TaskActions.AddFollower(this.task, user));
    } else {
      this.store.dispatch(new TaskActions.RemoveFollower(this.task, user));
    }
  }

  trackByFn(index: number, item: any): any {
    return item?.id || index;
  }
}
